import { Area, AreaChart, CartesianGrid, XAxis } from 'recharts';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty';
import { Legenda } from './Legenda';
import { asTijdvakLabel } from './tijdvak';
import type { OverzichtData } from './overzicht.api';

export function OntwikkelingKaart({ data }: { data: OverzichtData }) {
  const { reeksen, punten } = data.ontwikkeling;

  // Vijf reeksen, vijf kleuren: shadcn definieert precies --chart-1..5.
  const config: ChartConfig = Object.fromEntries(
    reeksen.map((r, i) => [r.id, { label: r.label, color: `var(--chart-${i + 1})` }]),
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Ontwikkeling hoofdonderwerpen</CardTitle>
        <CardDescription>
          {reeksen.length > 0
            ? `Opgeteld over ${data.periode.label} · de ${reeksen.length} meest genoemde onderwerpen`
            : data.periode.label}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {reeksen.length === 0 ? (
          <Empty>
            <EmptyHeader>
              <EmptyTitle>Niets gevonden in deze periode</EmptyTitle>
              <EmptyDescription>
                Zodra er gesprekken zijn verwerkt verschijnt hier het verloop per onderwerp.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <ChartContainer className="h-[280px] w-full" config={config}>
            <AreaChart accessibilityLayer data={punten} margin={{ left: 12, right: 12 }}>
              <CartesianGrid vertical={false} />
              <XAxis
                axisLine={false}
                dataKey="tijdvak"
                tickFormatter={asTijdvakLabel}
                tickLine={false}
                tickMargin={8}
                minTickGap={16}
              />
              <ChartTooltip
                content={<ChartTooltipContent className="min-w-52" indicator="dot" />}
                cursor={false}
              />
              {reeksen.map((r) => (
                <Area
                  dataKey={r.id}
                  fill={`var(--color-${r.id})`}
                  // Doorschijnende vulling met een volle rand: zo blijft de band
                  // rustig en is de lijn er bovenop toch scherp. De staven in de
                  // grafiek eronder krijgen dezelfde behandeling, zodat de twee
                  // grafieken bij elkaar horen.
                  fillOpacity={0.4}
                  key={r.id}
                  stackId="a"
                  stroke={`var(--color-${r.id})`}
                  type="monotone"
                />
              ))}
            </AreaChart>
          </ChartContainer>
        )}
        <Legenda reeksen={reeksen} />
      </CardContent>
    </Card>
  );
}
